import React, { useRef } from 'react'

import { Box, Color, DisplayPosition, ZIndex } from '@gousto-internal/citrus-react'
import { DeviceType, useDeviceType } from '@library/dom'
import type { Recipe, RecipeUUID } from '@library/menu-service'
import ReactModal from 'react-modal'

import { content, Drawer, overlay } from '../SwapAlternativeOptions/SwapOptionsCollapsable/styles'
import { useClickOutside } from '../SwapAlternativeOptions/useClickOutside'
import { FlexPortionEditor } from './FlexPortionEditor'

type FlexPortionTileEditorProps = {
  isOpen: boolean
  recipeCount: number
  recipeId: RecipeUUID
  numPortions: number
  portions: Recipe['portions']
  onClose: () => void
  onRemoveRecipe?: (recipeId: RecipeUUID) => void
}

const preventPropagation = (event: React.SyntheticEvent) => event.stopPropagation()

export const FlexPortionTileEditor = ({
  isOpen,
  recipeCount,
  recipeId,
  numPortions,
  portions,
  onClose,
  onRemoveRecipe,
}: FlexPortionTileEditorProps) => {
  const deviceType = useDeviceType()
  const editorRef = useRef(null)

  useClickOutside(editorRef, onClose, [isOpen])

  const editor = (
    <FlexPortionEditor
      recipeCount={recipeCount}
      numPortions={numPortions}
      portions={portions}
      onClose={onClose}
      // TODO: Wire this into basket portion updates once the Flex basket API is available.
      onUpdatePortions={onClose}
      onRemoveRecipe={() => {
        onRemoveRecipe?.(recipeId)
        onClose()
      }}
    />
  )

  if (!isOpen) {
    return null
  }

  if (deviceType === DeviceType.MOBILE) {
    return (
      <ReactModal isOpen={isOpen} style={{ overlay, content }}>
        <Drawer onClick={preventPropagation} onKeyPress={preventPropagation}>
          <Box
            ref={editorRef}
            onKeyDown={preventPropagation}
            onKeyUp={preventPropagation}
            width="100%"
          >
            {editor}
          </Box>
        </Drawer>
      </ReactModal>
    )
  }

  return (
    <Box
      position={DisplayPosition.Absolute}
      bottom="0"
      left="0"
      right="0"
      width="100%"
      onClick={preventPropagation}
      onKeyDown={preventPropagation}
      onKeyPress={preventPropagation}
      onKeyUp={preventPropagation}
    >
      <Box ref={editorRef} position={DisplayPosition.Relative}>
        <Box
          bg={Color.White}
          boxShadow="0 18px 14px 0 rgba(51, 61, 71, 0.2)"
          data-testid="flex-portion-tile-editor"
          position={DisplayPosition.Absolute}
          top="1px"
          width="100%"
          zIndex={ZIndex.Mask}
        >
          {editor}
        </Box>
      </Box>
    </Box>
  )
}
